/* Siemens Mermaid theme patch for MkDocs Material
 *
 * MkDocs Material renders Mermaid diagrams inside a closed shadow DOM and
 * injects its own themeCSS string. We rely on the docs-theme / Material
 * defaults for the bulk of Mermaid styling and only append a handful of
 * Portal-specific rules:
 *
 *   1. Predefined brand "class" tokens (primary, secondary, accent, ...) so
 *      authors can write `class A primary` or `A:::primary` without a
 *      `classDef` block. Colors resolve to CSS custom properties defined in
 *      `_mermaid.scss`, so a page can rebind any of them locally.
 *
 *   2. A small fix that lets per-diagram `classDef color:` declarations win
 *      over Material's `.nodeLabel p { color: ... }` rule.
 *
 *   3. Sequence-diagram arrowhead fill. Mermaid prefixes element IDs (e.g.
 *      `__mermaid_0_arrowhead`), so Material's exact `#arrowhead` selector
 *      misses them and the arrowheads render in the wrong color (or invisible)
 *      against the dark page.
 *
 *   4. Journey-diagram theming. Material has no built-in CSS for journey
 *      sections, tasks, and faces — the defaults look washed out on a dark
 *      page — so we map them to the Portal palette from `_mermaid.scss`.
 *
 * Loaded via extra_javascript in mkdocs.yml. Must run before mermaid.initialize().
 */

(() => {
  // Brand "class" tokens. Each name resolves to two CSS custom properties
  // defined in `_mermaid.scss`:
  //   --md-mermaid-class-<name>-bg  : node fill (and default stroke)
  //   --md-mermaid-class-<name>-fg  : label text color
  // To override the stroke independently, set `--md-mermaid-class-<name>-stroke`.
  const brandClasses = ["primary", "secondary", "accent", "success", "warning", "danger", "neutral", "highlight"];

  const brandClassCss = brandClasses
    .map((name) => {
      const bg = `var(--md-mermaid-class-${name}-bg)`;
      const fg = `var(--md-mermaid-class-${name}-fg)`;
      const stroke = `var(--md-mermaid-class-${name}-stroke,${bg})`;
      return [
        // Flowchart / state / class node shapes
        `.node.${name} > rect,`,
        `.node.${name} > polygon,`,
        `.node.${name} > circle,`,
        `.node.${name} > ellipse,`,
        `.node.${name} > path`,
        `{fill:${bg};stroke:${stroke}}`,
        // Label text inside the node
        `.node.${name} .nodeLabel,`,
        `.node.${name} .nodeLabel p`,
        `{color:${fg};fill:${fg}}`,
      ].join("");
    })
    .join("");

  const extraThemeCss = [
    // -- classDef text-color fix ---------------------------------------------
    // Material's themeCSS sets `.nodeLabel p { color: var(--md-mermaid-label-fg-color) }`
    // which overrides classDef text color.  Inheriting lets classDef win.
    ".nodeLabel p{color:inherit}",

    // -- Sequence-diagram arrowhead fix --------------------------------------
    // Mermaid prefixes IDs (e.g. `__mermaid_0_arrowhead`), so Material's exact
    // `#arrowhead` / `#sequencenumber` selectors do not match. Re-apply the
    // sequence message-line color so arrowheads share their line's color.
    '[id$="arrowhead"] path{fill:var(--md-mermaid-sequence-message-line-color);stroke:none}',
    '[id$="sequencenumber"]{fill:var(--md-mermaid-sequence-number-bg-color)!important}',

    // -- Journey diagram theming ---------------------------------------------
    // Material has no built-in CSS for journey diagrams; pick up the Portal
    // palette from `_mermaid.scss` so sections, tasks, and faces don't render
    // in Mermaid's washed-out pastel defaults.
    ".legend,.journey-section,text.task,text:not([class]){fill:var(--md-mermaid-label-fg-color)}",
    ".task-line{stroke:var(--md-mermaid-edge-color)}",
    ".mouth{stroke:var(--md-mermaid-journey-mouth-color,var(--md-mermaid-edge-color))}",
    "rect.journey-section,rect.task{fill:var(--md-mermaid-node-bg-color);stroke:var(--md-mermaid-edge-color)}",
    "rect.section-type-0,rect.task-type-0{fill:var(--md-mermaid-journey-section-0-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-1,rect.task-type-1{fill:var(--md-mermaid-journey-section-1-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-2,rect.task-type-2{fill:var(--md-mermaid-journey-section-2-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-3,rect.task-type-3{fill:var(--md-mermaid-journey-section-3-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-4,rect.task-type-4{fill:var(--md-mermaid-journey-section-4-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-5,rect.task-type-5{fill:var(--md-mermaid-journey-section-5-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-6,rect.task-type-6{fill:var(--md-mermaid-journey-section-6-bg,var(--md-mermaid-node-bg-color))}",
    "rect.section-type-7,rect.task-type-7{fill:var(--md-mermaid-journey-section-7-bg,var(--md-mermaid-node-bg-color))}",
    ".face{fill:var(--md-mermaid-journey-face-bg,var(--md-mermaid-label-bg-color));stroke:var(--md-mermaid-edge-color)}",

    // -- Brand "class" tokens ------------------------------------------------
    // Predefined Siemens brand classes usable as `class A primary` or `A:::primary`.
    brandClassCss,
  ].join("");

  const patchMermaid = (instance) => {
    if (!instance || instance.__siemensMermaidThemePatched) {
      return;
    }

    const originalInitialize = typeof instance.initialize === "function" ? instance.initialize.bind(instance) : null;

    if (!originalInitialize) {
      return;
    }

    instance.initialize = (config = {}) =>
      originalInitialize({
        ...config,
        themeCSS: `${config.themeCSS ?? ""}${extraThemeCss}`,
      });

    Object.defineProperty(instance, "__siemensMermaidThemePatched", {
      value: true,
      configurable: false,
      enumerable: false,
      writable: false,
    });
  };

  let currentMermaid = window.mermaid;
  patchMermaid(currentMermaid);

  const descriptor = Object.getOwnPropertyDescriptor(window, "mermaid");
  if (!descriptor || descriptor.configurable) {
    Object.defineProperty(window, "mermaid", {
      configurable: true,
      enumerable: descriptor?.enumerable ?? true,
      get() {
        return currentMermaid;
      },
      set(value) {
        currentMermaid = value;
        patchMermaid(value);
      },
    });
  }
})();
